import React, { useEffect, useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { consoleLogInDev } from 'shared';
import { Checkbox, OutlinedDropdown, ToggleButtons, SecondaryButton, Colors } from 'library';
import { useLocation, useHistory } from 'react-router-dom';
import ShiftList from './Shifts/ShiftList';
import BusinessCalendar from './BusinessCalendar/BusinessCalendar';
import { IBusinessLocation } from 'models';
import { getBusinessLocations } from 'api/BusinessLocationApi';
import DraftList from './Drafts/DraftList';
import queryString from 'query-string';

const BIZ_TABS = ['Schedule', 'Job Listings', 'Drafts'];
const [calendar, jobs, drafts] = BIZ_TABS;

const JobCardList = () => {
    const [businessLocations, setBusinessLocations] = useState<IBusinessLocation[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<IBusinessLocation>();
    const location = useLocation();
    const history = useHistory();
    const queryParams = queryString.parse(location.search);
    const [activeJobs, setActiveJobs] = useState<boolean>(true);
    const [expiredJobs, setExpiredJobs] = useState<boolean>(false);

    const selectedTab = getSelectedTab(queryParams.initialTab as string);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('md'));

    function getSelectedTab(tab: string | null) {
        switch (tab?.toLocaleLowerCase()) {
            case 'drafts':
                return drafts;
            case 'job listings':
                return jobs;
            default:
                return calendar;
        }
    }

    function toggleView(tab: string) {
        const params = new URLSearchParams(window.location.search);
        params.set('initialTab', tab);
        const url = `jobs?${params.toString()}`;
        history.push(url);
    }

    useEffect(() => {
        fetchBusinessLocations();
    }, []);

    function fetchBusinessLocations() {
        getBusinessLocations()
            .then((result) => {
                const paramLocation = new URLSearchParams(window.location.search).get('location');
                const location = result.results.find((l: IBusinessLocation) => l.id === parseInt(paramLocation || ''));

                setSelectedLocation(location ?? result.results[0]);
                setBusinessLocations(result.results);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }

    function handleLocationChange(e: SelectChangeEvent<string | number | null>) {
        const params = new URLSearchParams(window.location.search);
        params.set('location', String(e.target.value));
        const url = `jobs?${params.toString()}`;
        history.push(url);
    }

    function tabFactory(tab: string) {
        const commonProps = {
            addressId: selectedLocation?.address.id,
            businessLocationId: selectedLocation?.id,
            timezone: selectedLocation?.timezone,
        };
        switch (tab) {
            case 'Jobs':
            case 'Job Listings':
                return <ShiftList {...commonProps} active={activeJobs} expired={expiredJobs} />;
            case 'Drafts':
                return <DraftList />;
            default:
                return <BusinessCalendar {...commonProps} />;
        }
    }

    const tabsOptions = BIZ_TABS;

    return (
        <Grid container>
            <Grid
                container
                item
                direction="row"
                justifyContent="space-between"
                style={{
                    backgroundColor: Colors.lightTurq,
                    paddingTop: '2%',
                    paddingBottom: '2%',
                    paddingLeft: '1%',
                    paddingRight: '1%',
                }}
            >
                <Grid container item lg={3} md={6} sm={12} alignItems="center" style={{ marginBottom: 10 }}>
                    <OutlinedDropdown
                        value={selectedLocation?.id.toString() || ''}
                        fields={businessLocations.map((b) => {
                            return { key: b.id.toString(), value: b.name };
                        })}
                        onChange={handleLocationChange}
                        textLabel="Location"
                    />
                </Grid>
                <Grid item container lg={5} md={6} sm={12} alignItems="center" style={{ marginBottom: 10 }}>
                    <ToggleButtons tabs={tabsOptions} onTabSelect={toggleView} selectedTab={selectedTab} />
                </Grid>

                {!isXs ? (
                    <Grid
                        container
                        item
                        lg={3}
                        md={6}
                        sm={12}
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ marginBottom: 5 }}
                    >
                        <Grid item sm={8} xs={6}>
                            {selectedLocation ? (
                                <SecondaryButton
                                    onClick={() => history.push(`/clock-in/?loc_id=${selectedLocation.address.id}`)}
                                >
                                    Access QR Code
                                </SecondaryButton>
                            ) : null}
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>

            <Grid item xs={12}>
                <div style={{ borderBottom: `1px solid ${Colors.mediumGrey}` }} />
            </Grid>
            {selectedTab === jobs && (
                <Grid item container direction="row" style={{ paddingTop: 15 }}>
                    <Checkbox
                        label="Active jobs"
                        checked={activeJobs}
                        onChange={() => setActiveJobs(!activeJobs)}
                        style={{ width: 'fit-content' }}
                    />
                    <Checkbox
                        label="Expired jobs"
                        checked={expiredJobs}
                        onChange={() => setExpiredJobs(!expiredJobs)}
                        style={{ width: 'fit-content', marginLeft: 30 }}
                    />
                </Grid>
            )}
            <Grid container direction="row" style={{ marginTop: 10 }} xs={12}>
                {tabFactory(selectedTab)}
            </Grid>
        </Grid>
    );
};

export default JobCardList;
